import { store, getContext, getElement } from '@wordpress/interactivity';

type ServerState = {
	state: {
		sku: string;
		selectedSwatchPatterns: {
			[key: string]: string | null;
		};
		allSwatches: {
			[key: string]: {
				[key: string]: Swatch[];
			};
		};
		activeSwatches: activeSwatches;
		selectedSwatches: {
			[key: string]: {
				[key: string]: Swatch | null;
			};
		};
		isAllFieldsSelected: boolean; // Do all swatch fields have a selected swatch?
		isDisabledState: boolean;
		getSelectedSwatchText: string;
		clientWidth: number;
		dialogRef: HTMLDialogElement;
		dialogSwatch: {
			attribute: string;
			application: string;
			item: Swatch;
		};
	};
};

type activeSwatches = Swatch[] | null;

type Swatch = {
	'swatch-name': string;
	'swatch-image': {
		src: string;
		alt: string;
		srcset: string;
	};
};

type ApplicationContainer = {
	attribute: string;
	application: string;
};

type SwatchButtonContext = {
	attribute: string;
	application: string;
	item: Swatch;
};

declare const jQuery: any;

const getTypedSwatchButtonContext = getContext<SwatchButtonContext>;

const form = document.querySelector('form.cart') as HTMLFormElement | null;
const addToQuoteButton = document.querySelector(
	'a.add-request-quote-button'
) as HTMLAnchorElement | null;

const addToCartButton = document.querySelector(
	'button.single_add_to_cart_button'
) as HTMLButtonElement | null;

const selectedSwatchesInput = form.querySelector(
	'input[name=selected-swatches]'
) as HTMLInputElement;

const storeDef = {
	state: {
		get activeSwatches(): activeSwatches {
			const { attribute } = getContext<ApplicationContainer>();
			const thisAttributesPattern =
				state.selectedSwatchPatterns[attribute];
			if (!thisAttributesPattern) {
				return null;
			}
			return state.allSwatches[attribute][thisAttributesPattern];
		},

		get isSwatchSelected(): boolean {
			const { attribute, application, item } =
				getTypedSwatchButtonContext();
			const applicationState =
				state.selectedSwatches[attribute][application];
			return item['swatch-name'] === applicationState['swatch-name'];
		},

		get isDisabledState(): boolean {
			const { attribute } = getContext<ApplicationContainer>();

			return state.selectedSwatchPatterns[attribute] === null;
		},

		get selectedSwatch(): Swatch | null {
			const { attribute } = getContext<ApplicationContainer>();
			const { application } = getContext<{ application: string }>();
			if (
				state.isDisabledState ||
				state.selectedSwatches[attribute][application] === null
			) {
				return null;
			}
			return state.selectedSwatches[attribute][application];
		},

		get selectedSwatchText(): string {
			if (state.selectedSwatch === null) {
				return 'None';
			}
			return state.selectedSwatch['swatch-name'];
		},

		get isAllFieldsSelected(): boolean {
			return Object.values(state.selectedSwatches).every(
				(v) => Object.values(v)[0] !== null
			);
		},
	},

	actions: {
		init: () => {
			callbacks.initEventListener();
			tooltip.updateClientWidth();
		},
		selectSwatch: () => {
			const { attribute, application, item } =
				getTypedSwatchButtonContext();
			state.dialogSwatch = {
				attribute,
				application,
				item,
			};
			dialog.openDialog();
		},
		watchSelectedSwatches: () => {
			if (!state.selectedSwatches) {
				return;
			}
			selectedSwatchesInput.setAttribute(
				'value',
				JSON.stringify(state.selectedSwatches)
			);
		},
		canSubmitCart: () => {
			if (state.isAllFieldsSelected) {
				callbacks.enableElement(addToCartButton);
				callbacks.enableElement(addToQuoteButton);
			} else {
				callbacks.disableElement(addToCartButton);
				callbacks.disableElement(addToQuoteButton);
			}
		},
	},
	tooltip: {
		updateClientWidth: () => {
			state.clientWidth = document.documentElement.clientWidth;
		},
		calculateTooltipBounds: () => {
			// Ensures tooltip is always at least 4px from the edge of the viewport
			// Watches state.clientWidth to update when it's modified
			if (state.clientWidth) {
				const { ref } = getElement();
				if (!ref) {
					return;
				}
				const tooltip = ref as HTMLSpanElement;
				const boundingClientRect = tooltip.getBoundingClientRect();
				const xAxisValue = boundingClientRect.x;

				const inBoundsLeftValue = 4;
				const isOutOfBoundsLeft = xAxisValue < inBoundsLeftValue;

				if (isOutOfBoundsLeft) {
					// If negative number, transform positive X + 4
					const transformValue = 4 - xAxisValue;
					const spacingUnitConversion = transformValue / 4;
					ref.setAttribute(
						'style',
						`transform: translateX(calc(var(--spacing) * ${spacingUnitConversion}))`
					);
					return;
				}

				const widthValue = boundingClientRect.width;
				const inBoundsRightValue =
					document.documentElement.clientWidth - 4;
				const isOutOfBoundsRight =
					xAxisValue + widthValue > inBoundsRightValue;

				if (isOutOfBoundsRight) {
					const transformValue =
						xAxisValue + widthValue - inBoundsRightValue;
					const spacingUnitConversion = -(transformValue / 4);
					ref.setAttribute(
						'style',
						`transform: translateX(calc(var(--spacing) * ${spacingUnitConversion}))`
					);
				}
			}
		},
	},
	dialog: {
		init: () => {
			const { ref } = getElement();
			if (!ref) {
				return;
			}
			state.dialogRef = ref as HTMLDialogElement;
		},
		confirmSwatch: () => {
			const { attribute, application, item } = state.dialogSwatch;

			// Creating a new state variable, as opposed to mutating the current state, so derived values are re-calculated i.e isAllFieldsSelected
			// @see https://react.dev/learn/updating-objects-in-state#copying-objects-with-the-spread-syntax
			const newState = {
				...state.selectedSwatches,
			};
			newState[attribute][application] = item;

			state.selectedSwatches = newState;
			dialog.closeDialog();
		},
		openDialog: () => {
			state.dialogRef.showModal();
			document.documentElement.classList.add('has-modal-open');
		},
		closeDialog: () => {
			state.dialogRef.close();
			document.documentElement.classList.remove('has-modal-open');
		},
	},
	callbacks: {
		enableElement: (element: Element | null) => {
			if (!element) {
				return;
			}
			element.removeAttribute('disabled');
			element.classList.remove('disabled');
		},
		disableElement: (element: Element | null) => {
			if (!element) {
				return;
			}
			element.setAttribute('disabled', '');
			element.classList.add('disabled');
		},
		initEventListener: () => {
			if (typeof jQuery !== 'undefined') {
				jQuery('form.variations_form').on(
					'show_variation',
					(event: any, variation: { [key: string]: any }) => {
						const variationSKU = variation.sku;
						if (variationSKU && typeof variationSKU === 'string') {
							state.sku = variationSKU;
						}
					}
				);
			}

			const applications = Object.keys(state.selectedSwatchPatterns);

			applications.forEach((key) => {
				const select = document.getElementById(
					key
				) as HTMLSelectElement;

				// Runs on page load, when adding a product to the cart the value persists but visible swatch state does not
				if (select.value) {
					state.selectedSwatchPatterns[key] = select.value;
				}

				// Changing selected attribute modifies the visible swatches
				select.addEventListener('change', (e: Event) => {
					const target = e.target as HTMLSelectElement;
					state.selectedSwatchPatterns[key] = target.value;
					callbacks.clearSelectedSwatches();

					// 'Choose an option' selected
					if (target.value === '') {
						state.selectedSwatchPatterns[key] = null;
					}
				});

				// WooCommerce only renders the reset_variations button once per page, not per key
				const resetLink =
					select.nextElementSibling as HTMLAnchorElement;
				if (resetLink.nodeName !== 'A') {
					return;
				}

				// Clearing the selected attribute with the 'clear' button resets the visible swatches
				resetLink.addEventListener('click', () => {
					state.selectedSwatchPatterns[key] = null;
				});

				resetLink.addEventListener('keydown', (e) => {
					if (e.key !== 'Enter') {
						return;
					}
					state.selectedSwatchPatterns[key] = null;
				});
			});

			// Adding to cart adds the selected swatches as line items
			if (!form) {
				return;
			}

			form.addEventListener('submit', () => {
				const formattedSelectedSwatches = formatSelectedSwatches();

				selectedSwatchesInput.setAttribute(
					'value',
					formattedSelectedSwatches
				);
			});
		},
		clearSelectedSwatches: () => {
			for (const [groupKey, groupValue] of Object.entries(
				state.selectedSwatches
			)) {
				for (const [applicationKey, applicationValue] of Object.entries(
					groupValue
				)) {
					if (applicationValue) {
						groupValue[applicationKey] = null;
					}
				}
			}
		},
	},
};

type Store = ServerState & typeof storeDef;

const { state, actions, callbacks, tooltip, dialog } = store<Store>(
	'product-attribute-colour-selector',
	storeDef
);

const productPriceStore: {
	state: {
		productId: number;
		variationId: number | null;
	};
} = store('product-price-updates');

function formatSelectedSwatches() {
	const selectedSwatches = state.selectedSwatches;

	const formattedSwatches = Object.keys(selectedSwatches).map((attribute) => {
		const terms = selectedSwatches[attribute];

		return Object.keys(terms).map((term) => {
			const swatch: Swatch = terms[term];
			if (swatch) {
				return {
					name: swatch['swatch-name'],
					application: term,
				};
			}
		});
	});
	return JSON.stringify(formattedSwatches);
}
